import TimetrackingApi from '../../common/api/zeiterfassungServerApi/TimetrackingApi';
import { Employee } from '~/common/entities/Department/DepartmentTypes';
import { Table, Thead, Tbody, Tr, Th } from '@chakra-ui/react';
import { formatName } from '../../common/utils/employeeUtils';
import useTranslate from '~/common/utils/translate';
import { EmployeeHeader } from './EmployeeHeader';
import { DayData, DayStatus } from './DayData';
import { useTimetrackingStore } from './utils';
import { DayHeader } from './DayHeader';
import { isSameDay } from 'date-fns';
import React from 'react';
import _ from 'lodash';

interface EmployeeCalendarViewProps {
  employees: Employee[];
  days: Date[];
}
export const EmployeeCalendarView: React.FC<EmployeeCalendarViewProps> = ({
  employees,
  days,
}) => {
  const t = useTranslate();
  const { currentDate } = useTimetrackingStore();

  const { data: offDays } =
    TimetrackingApi.hooks.useOffDaysOfMonth(currentDate);
  const { data: workDays } =
    TimetrackingApi.hooks.useWorkDaysOfMonth(currentDate);
  const getDayStatus = (day: Date, employee: Employee): DayStatus => {
    const employeeOffDay = offDays?.find(
      (od) => isSameDay(new Date(od.date), day) && od.employee === employee.id,
    );

    if (employeeOffDay) {
      switch (employeeOffDay.type) {
        case 'PUBLIC_HOLIDAY':
          return 'PUBLIC_HOLIDAY';
        case 'SICK':
          return 'SICK';
        case 'HOLIDAY':
          return 'HOLIDAY';
        case 'OTHER':
          return 'OTHER';
        default:
          return employeeOffDay.type as DayStatus;
      }
    }

    const isWorkDay = workDays?.some(
      (wd) =>
        isSameDay(new Date(wd.date), day) &&
        wd.employee_id === (employee.id as unknown as number) &&
        wd.is_working,
    );

    return isWorkDay ? 'WORKDAY' : 'NON_WORKDAY';
  };

  const getNameOfDay = (day: Date, employee: Employee): string => {
    const employeeOffDay = offDays?.find(
      (od) => isSameDay(new Date(od.date), day) && od.employee === employee.id,
    );
    if (employeeOffDay) {
      return employeeOffDay.name;
    }
    return '';
  };

  return (
    <Table size="sm">
      <Thead>
        <Tr>
          <Th>{t('timeTracking.employees')}</Th>
          {days.map((day) => (
            <DayHeader key={`dh-${day.toISOString()}`} day={day} />
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {_.orderBy(employees, (emp: Employee) => formatName(emp)).map(
          (employee: Employee) => (
            <Tr key={employee.id} borderTop="1px" borderBottom="1px">
              <EmployeeHeader employee={employee} />
              {days.map((day) => (
                <DayData
                  key={`dr-${day.toISOString()}`}
                  day={day}
                  employee={employee}
                  dayStatus={getDayStatus(day, employee)}
                  name={getNameOfDay(day, employee)}
                />
              ))}
            </Tr>
          ),
        )}
      </Tbody>
    </Table>
  );
};
